import { Formik } from 'formik'
import { isEmpty, keys } from 'lodash'
import { ReactElement, useState } from 'react'
import { defineMessages, useIntl, FormattedMessage } from 'react-intl'
import { useNavigate } from 'react-router-dom'

import { Input } from 'components/Input'
import { Select } from 'components/v2/Select'
import { Tag } from 'components/v2/Tag'
import { PlaceAutocomplete } from 'features/Places/components/PlaceAutocomplete'
import { BOOLEAN } from 'utils/boolean'
import { getQueryString } from 'utils/route'
import {
  FilterHeader,
  FilterToggle,
  FilterWrapper,
  FiltersContainer,
  FiltersSubmit,
  FiltersWrapper,
  TagsWrapper
} from 'components/Filters'
import { CompanyAutocomplete } from 'features/Companies/components/CompanyAutocomplete'
import { ICompanyAutocomplete } from 'features/Companies/components/MultiCompanyAutocomplete'
import { RouteAutocomplete } from 'features/Routes/components/RouteAutocomplete'
import { BusinessClientAutocomplete } from 'features/BusinessClients/components/BusinessClientAutocomplete'
import { BusinessSiteAutocomplete } from 'features/BusinessSites/components/BusinessSiteAutocomplete'

const messages = defineMessages({
  selectCompany: {
    id: 'routes.filters.selectCompany',
    defaultMessage: 'Select company'
  },
  selectRoute: {
    id: 'routes.filters.selectRoute',
    defaultMessage: 'Select route'
  },
  selectOrigin: {
    id: 'routes.filters.selectOrigin',
    defaultMessage: 'Select origin'
  },
  selectDestination: {
    id: 'routes.filters.selectDestination',
    defaultMessage: 'Select destination'
  },
  serviceCodePlaceholder: {
    id: 'routes.filters.servciceCode.placeholder',
    defaultMessage: 'E.g: CR041022'
  },
  onlyActiveServicesValue: {
    id: 'routes.filters.onlyActiveServices.value',
    defaultMessage: 'Only active'
  },
  allServices: {
    id: 'routes.filters.onlyActiveServices.all.value',
    defaultMessage: 'All'
  },
  selectBusinessClient: {
    id: 'routes.filters.selectBusinessClient',
    defaultMessage: 'Select business client'
  },
  selectBusinessSite: {
    id: 'routes.filters.selectBusinessSite',
    defaultMessage: 'Select business site'
  }
})

const defaultValues = {
  route: { id: '', name: '' },
  company: { id: '', name: '' },
  origin: { name: '' },
  destination: { name: '' },
  serviceCode: '',
  onlyActiveServices: BOOLEAN.TRUE,
  businessClient: { id: '', name: '' },
  businessSite: { id: '', name: '' }
}

export const RoutesFilters = ({ filters = {} }: TSFixMe): ReactElement => {
  const intl = useIntl()
  const navigate = useNavigate()
  const [isShowingFilters, showFilters] = useState(false)

  const toggleFilters = (): TSFixMe => {
    showFilters(!isShowingFilters)
  }

  const filterRoutes = (values: TSFixMe): TSFixMe => {
    const query = {
      companyId: values.company.id,
      origin: values.origin.id,
      destination: values.destination.id,
      serviceCode: values.serviceCode,
      onlyActiveServices: values.onlyActiveServices,
      routeName: values.route.name,
      businessClientId: values.businessClient.id,
      businessSiteId: values.businessSite.id
    }

    navigate(`/routes?${getQueryString(query)}`)
  }

  const initialValues = {
    company: filters.company || defaultValues.company,
    route: filters.route || defaultValues.route,
    origin: filters.origin || defaultValues.origin,
    destination: filters.destination || defaultValues.destination,
    serviceCode: filters.serviceCode || defaultValues.serviceCode,
    onlyActiveServices: filters.onlyActiveServices || defaultValues.onlyActiveServices,
    businessClient: filters.businessClient || defaultValues.businessClient,
    businessSite: filters.businessSite || defaultValues.businessSite
  }

  return (
    <Formik initialValues={initialValues} onSubmit={filterRoutes} enableReinitialize>
      {(props): ReactElement => {
        const { handleSubmit, handleChange, setFieldValue, values } = props

        const handleFilterDelete = (id: keyof typeof defaultValues): void => {
          const defaultValue = defaultValues[id]
          filterRoutes({
            ...values,
            [id]: defaultValue
          })
        }

        const handleSelectChange = (event: TSFixMe): void => {
          const {
            target: { id, value }
          } = event

          handleChange(event)

          filterRoutes({
            ...values,
            [id]: value
          })
        }

        return (
          <form onSubmit={handleSubmit}>
            <div>
              <FilterHeader>
                <FilterWrapper>
                  <div>
                    <FilterToggle onClick={toggleFilters} />
                  </div>
                  <TagsWrapper>
                    {keys(filters).map((filterKey): TSFixMe => {
                      let label, value
                      const filterValue = filters[filterKey]
                      const hasValue = !isEmpty(filterValue)

                      if (!hasValue) return null

                      switch (filterKey) {
                        case 'onlyActiveServices':
                          if (filterValue === BOOLEAN.TRUE) return null

                          label = (
                            <FormattedMessage id="routes.filters.onlyActiveServices.label" defaultMessage="Services" />
                          )
                          value = intl.formatMessage(messages.allServices)
                          break
                        case 'company':
                          if (!filterValue.id) return null

                          label = <FormattedMessage id="dictionary.company" defaultMessage="Company" />
                          value = filterValue.name
                          break
                        case 'route':
                          if (!filterValue.name) return null

                          label = <FormattedMessage id="dictionary.route" defaultMessage="Route" />
                          value = filterValue.name
                          break
                        case 'origin':
                          if (!filterValue.id) return null

                          label = <FormattedMessage id="dictionary.origin" defaultMessage="Origin" />
                          value = filterValue.name
                          break
                        case 'destination':
                          if (!filterValue.id) return null

                          label = <FormattedMessage id="dictionary.destination" defaultMessage="Destination" />
                          value = filterValue.name
                          break
                        case 'serviceCode':
                          label = (
                            <FormattedMessage id="routes.filters.serviceCode.tag.label" defaultMessage="Service code" />
                          )
                          value = filterValue
                          break
                        case 'businessClient':
                          if (!filterValue.id) return null

                          label = <FormattedMessage id="dictionary.businessClient" defaultMessage="Business client" />
                          value = filterValue.name
                          break
                        case 'businessSite':
                          if (!filterValue.id) return null

                          label = <FormattedMessage id="dictionary.businessSite" defaultMessage="Business site" />
                          value = filterValue.name
                          break
                      }

                      return (
                        <Tag
                          key={filterKey}
                          onDelete={handleFilterDelete.bind(this, filterKey as keyof typeof defaultValues)}
                        >
                          {label}: {value}
                        </Tag>
                      )
                    })}
                  </TagsWrapper>
                </FilterWrapper>
              </FilterHeader>
              <FiltersWrapper isShowingFilters={isShowingFilters}>
                <FiltersContainer>
                  <Select
                    id="onlyActiveServices"
                    value={values.onlyActiveServices}
                    onChange={handleSelectChange}
                    label={<FormattedMessage id="routes.filters.onlyActiveServices.label" defaultMessage="Services" />}
                  >
                    <option value={BOOLEAN.FALSE}>{intl.formatMessage(messages.allServices)}</option>
                    <option value={BOOLEAN.TRUE}>{intl.formatMessage(messages.onlyActiveServicesValue)}</option>
                  </Select>
                  <CompanyAutocomplete
                    placeholder={intl.formatMessage(messages.selectCompany)}
                    value={values.company.name}
                    label={<FormattedMessage id="dictionary.company" defaultMessage="Company" />}
                    onChange={(company: ICompanyAutocomplete): void => {
                      filterRoutes({
                        ...values,
                        company
                      })
                    }}
                  />
                  <RouteAutocomplete
                    placeholder={intl.formatMessage(messages.selectRoute)}
                    value={values.route.name}
                    label={<FormattedMessage id="dictionary.route" defaultMessage="Route" />}
                    onChange={(routes): void => {
                      const route = isEmpty(routes) ? defaultValues.route : routes.at(0)
                      setFieldValue('route', route)

                      filterRoutes({
                        ...values,
                        route
                      })
                    }}
                    uniqueName
                  />
                  <PlaceAutocomplete
                    placeholder={intl.formatMessage(messages.selectOrigin)}
                    value={values.origin.name}
                    label={<FormattedMessage id="dictionary.origin" defaultMessage="Origin" />}
                    onChange={(place: TSFixMe): TSFixMe => {
                      setFieldValue('origin', place)

                      filterRoutes({
                        ...values,
                        origin: place
                      })
                    }}
                  />
                  <PlaceAutocomplete
                    placeholder={intl.formatMessage(messages.selectDestination)}
                    value={values.destination.name}
                    label={<FormattedMessage id="dictionary.destination" defaultMessage="Destination" />}
                    onChange={(place: TSFixMe): TSFixMe => {
                      setFieldValue('destination', place)

                      filterRoutes({
                        ...values,
                        destination: place
                      })
                    }}
                  />
                  <Input
                    id="serviceCode"
                    placeholder={intl.formatMessage(messages.serviceCodePlaceholder)}
                    value={values.serviceCode}
                    label={<FormattedMessage id="routes.filters.serviceCode.label" defaultMessage="Service code" />}
                    onChange={handleChange}
                  />
                  <BusinessClientAutocomplete
                    id="businessClient"
                    placeholder={intl.formatMessage(messages.selectBusinessClient)}
                    value={values.businessClient.name}
                    label={<FormattedMessage id="dictionary.businessClient" defaultMessage="Business client" />}
                    onChange={(businessClient): void => {
                      setFieldValue('businessClient', businessClient)

                      filterRoutes({
                        ...values,
                        businessClient
                      })
                    }}
                  />
                  <BusinessSiteAutocomplete
                    id="businessSite"
                    placeholder={intl.formatMessage(messages.selectBusinessSite)}
                    value={values.businessSite.name}
                    label={<FormattedMessage id="dictionary.businessSite" defaultMessage="Business site" />}
                    onChange={(businessSite): void => {
                      setFieldValue('businessSite', businessSite)

                      filterRoutes({
                        ...values,
                        businessSite
                      })
                    }}
                  />
                </FiltersContainer>
              </FiltersWrapper>
            </div>
            <FiltersSubmit />
          </form>
        )
      }}
    </Formik>
  )
}
